<template>
  <div class="publication-template-step d-flex" data-testid="oc-publication-template-step">
    <section class="left-section">
      <div class="header-area">
        <div class="header m-4">
          <h3 class="mb-2">{{ $t('order_creation.publication_template.title') }}</h3>
          <p class="text-muted">{{ $t('order_creation.publication_template.subtitle') }}</p>
        </div>
        <hr class="m-0" />
      </div>
      <div class="templates-area">
        <div v-if="publicationTemplates.length > 0" class="publication-templates">
          <div
            class="template-wrapper"
            v-for="group in publicationTemplates"
            :key="group.key"
            :data-testid="`oc-publication-template-group-${group.key}`">
            <h4 class="mb-2">
              {{ group.title }} <span class="badge">{{ group.version }}</span>
            </h4>
            <div class="template-options">
              <!-- base template card (no variation), always first -->
              <div
                class="template-option"
                :data-testid="`oc-publication-template-option-${group.key}`"
                :class="{ 'is-active': selectedTemplateKey === group.key && !selectedVariationKey }"
                @click="changePublicationTemplate(group.key, null)">
                <div v-if="loadingImages[group.key]" class="template-loading">
                  <div class="spinner-border"></div>
                </div>
                <img
                  v-else-if="templatePreviews[group.key]"
                  :src="templatePreviews[group.key]"
                  class="template-preview-image"
                  :alt="group.title" />
                <div v-else class="template-fallback"></div>
                <div class="template-overlay"></div>
                <p class="template-name">{{ group.title }}</p>
              </div>
              <!-- one card per variation, to the right of the base -->
              <div
                class="template-option"
                v-for="variation in group.variations"
                :key="variation.key"
                :data-testid="`oc-publication-template-option-${group.key}-${variation.key}`"
                :class="{ 'is-active': selectedTemplateKey === group.key && selectedVariationKey === variation.key }"
                @click="changePublicationTemplate(group.key, variation.key)">
                <div v-if="loadingImages[previewKey(group.key, variation.key)]" class="template-loading">
                  <div class="spinner-border"></div>
                </div>
                <img
                  v-else-if="templatePreviews[previewKey(group.key, variation.key)]"
                  :src="templatePreviews[previewKey(group.key, variation.key)]"
                  class="template-preview-image"
                  :alt="variation.title" />
                <div v-else class="template-fallback"></div>
                <div class="template-overlay"></div>
                <p class="template-name">{{ variation.title }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="templatesLoaded" class="no-templates">
          <h4 class="mb-2">{{ $t('order_creation.publication_template.no_templates.title') }}</h4>
          <p class="text-muted">{{ $t('order_creation.publication_template.no_templates.subtitle') }}</p>
        </div>
      </div>
    </section>
    <section class="right-section">
      <div class="browser-chrome">
        <span class="browser-dot"></span>
        <span class="browser-dot"></span>
        <span class="browser-dot"></span>
      </div>
      <div class="iframe-wrapper">
        <PopsHostFrame
          v-if="selectedTemplateKey && loadedOptionsKey === previewKey(selectedTemplateKey, selectedVariationKey)"
          :key="previewKey(selectedTemplateKey, selectedVariationKey)"
          :templateKey="selectedTemplateKey"
          :variation="selectedVariationKey ?? undefined"
          :locale="order.language"
          mode="preview"
          :readonly="true"
          :parameters="popsParameters"
          :publication="publicationState"
          :options="templateOptions" />
      </div>
    </section>
  </div>
</template>
<script lang="ts" setup>
import { useOrderCreationStore } from '@/stores/orderCreation';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import PopsHostFrame from '@/components/orders/creation/subcomponents/PopsHostFrame.vue';
import { getPublicationTemplate, getPublicationTemplatePreviewImage } from '@/api/publication';
import type { TemplateMeta } from '@/types/templateMeta.js';
import type { PublicationTemplate } from '@/types/publication';

const allowNext = defineModel('allowNext', { type: Boolean, required: true });
const orderCreationStore = useOrderCreationStore();
const { order, publication, shouldCreatePublication, publicationTemplates } = storeToRefs(orderCreationStore);
const router = useRouter();

const templatePreviews = ref<Record<string, string>>({}); // preview image url per base/variation, keyed by previewKey()
const loadingImages = ref<Record<string, boolean>>({}); // track loading state per base/variation
const templateOptions = ref<Record<string, unknown>>({});
const loadedOptionsKey = ref<string | null>(null); // previewKey() of the last completed options fetch
const templatesLoaded = ref(false); // becomes true once the template list has been fetched (used to gate the empty-state)
const selectedTemplateKey = ref<string | null>(publication.value?.template ?? null);
const selectedVariationKey = ref<string | null>(publication.value?.variation ?? null);
let templateOptionsRequestId = 0;
let isFetchingPreviews = false;

onMounted(() => {
  if (!order.value.id) return;
  if (!currentPubKey.value) shouldCreatePublication.value = true;
  // lazy creation: only load the templates here. the publication is created when the user picks a
  // template (see changePublicationTemplate), so there is no fragile default-template guess.
  orderCreationStore.fetchPublicationTemplates().then(() => {
    templatesLoaded.value = true;
    ensureSelectedTemplate();
    void loadTemplateOptions(selectedTemplateKey.value ?? undefined, selectedVariationKey.value);
    // revisiting the step with an existing publication: allow proceeding immediately
    allowNext.value = !!selectedTemplateKey.value;
  });
});
/* -------------------------------- COMPUTED -------------------------------- */
const currentPubKey = computed(() => {
  return router.currentRoute.value.query.pubkey as string;
});
const popsParameters = computed(() => ({
  targetOrigin: window.location.origin,
  template: selectedTemplateKey.value,
}));
const publicationState = computed(() => {
  const template = selectedTemplateKey.value;
  if (!template) return undefined;

  return {
    name: publication.value?.name ?? '',
    pubkey: publication.value?.pubkey ?? '',
    template,
    variation: selectedVariationKey.value ?? undefined,
  };
});

/* --------------------------------- METHODS -------------------------------- */
// composite key mirroring the backend template-values bucket: base is the bare key, a variation is `template::variation`
function previewKey(templateKey: string, variationKey?: string | null): string {
  return variationKey ? `${templateKey}::${variationKey}` : templateKey;
}

// fetches the preview image for a template (base) or one of its variations.
// preview_image returns json ({ data: { '800x600' }, image }); the proxy url is used directly as <img src>.
async function fetchTemplatePreview(templateKey: string, variationKey?: string | null): Promise<void> {
  const key = previewKey(templateKey, variationKey);
  if (templatePreviews.value[key] || loadingImages.value[key]) return;

  try {
    loadingImages.value[key] = true;
    const res = await getPublicationTemplatePreviewImage(templateKey, variationKey ?? undefined);
    const url = res?.data?.['800x600'];
    if (url) templatePreviews.value[key] = url;
  } catch (error) {
    console.error(`Error fetching preview image for ${key}:`, error);
  } finally {
    loadingImages.value[key] = false;
  }
}

// fetches the base preview plus one preview per variation for every template group.
// runs at most 2 requests concurrently so the DRS renderer is not flooded.
// re-entrant calls are dropped: if a pool is already draining, a new one is not started.
async function fetchTemplatePreviews(templateGroups: PublicationTemplate[]): Promise<void> {
  if (isFetchingPreviews) return;
  isFetchingPreviews = true;
  const tasks: (() => Promise<void>)[] = [];

  for (const group of templateGroups) {
    tasks.push(() => fetchTemplatePreview(group.key));
    for (const variation of group.variations ?? []) {
      tasks.push(() => fetchTemplatePreview(group.key, variation.key));
    }
  }

  const CONCURRENCY = 2;
  let index = 0;

  async function worker() {
    while (index < tasks.length) {
      const task = tasks[index++];
      await task();
    }
  }

  try {
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  } finally {
    isFetchingPreviews = false;
  }
}

// gallery-only: demo_data makes a pretty template card. do not reuse this as a persist
// path — createCustomerPublication must not send template_values.
function buildTemplateOptions(meta: TemplateMeta): Record<string, unknown> {
  const opts: Record<string, unknown> = {};

  for (const def of meta.options ?? []) {
    const key = def.properties.key;
    if (!key) continue;

    if (meta.demo_data?.[key] !== undefined) {
      opts[key] = meta.demo_data[key];
    } else if (def.type === 'input.checkbox') {
      opts[key] = false;
    } else if (def.type === 'input.image') {
      opts[key] = null;
    } else if (def.type === 'input.complex') {
      opts[key] = def.properties.schema?.type === 'array' ? [] : {};
    } else {
      opts[key] = '';
    }
  }

  return opts;
}

async function loadTemplateOptions(templateKey?: string, variationKey?: string | null) {
  const requestId = ++templateOptionsRequestId;
  loadedOptionsKey.value = null;

  if (!templateKey) {
    templateOptions.value = {};
    return;
  }

  const key = previewKey(templateKey, variationKey);
  try {
    const response = await getPublicationTemplate(templateKey, variationKey);
    if (requestId !== templateOptionsRequestId) return;
    templateOptions.value = buildTemplateOptions(response.data);
    loadedOptionsKey.value = key;
  } catch (error) {
    if (requestId !== templateOptionsRequestId) return;
    console.error('Error fetching template options:', error);
    templateOptions.value = {};
    loadedOptionsKey.value = key;
  }
}

function ensureSelectedTemplate() {
  if (selectedTemplateKey.value) {
    return;
  }

  if (publication.value?.template) {
    selectedTemplateKey.value = publication.value.template;
    selectedVariationKey.value = publication.value.variation ?? null;
    return;
  }

  selectedTemplateKey.value = publicationTemplates.value[0]?.key ?? null;
  selectedVariationKey.value = null;
}

function selectTemplate(templateKey: string, variationKey: string | null) {
  selectedTemplateKey.value = templateKey;
  selectedVariationKey.value = variationKey;
}

function hasCurrentOrderPublication(): boolean {
  return (
    !!publication.value?.pubkey && !!order.value.id && Number(publication.value.order_id) === Number(order.value.id)
  );
}

// true when the current order already has a publication for this exact template+variation
// and the previous step did not request a full re-init (type / optimize / questions change).
function isSamePersistedSelection(templateKey: string, variationKey: string | null): boolean {
  if (!hasCurrentOrderPublication() || shouldCreatePublication.value) return false;
  const currentPublication = publication.value;
  if (!currentPublication) return false;
  return templateKey === currentPublication.template && variationKey === (currentPublication.variation ?? null);
}

// persist via create, not update: POST evals from/default and dispatches transform_content.
// an in-place PUT of template/variation does not rerun autogen, so going back from the editor
// and picking a different template would land there with empty content.
async function persistSelectedTemplate(): Promise<boolean> {
  ensureSelectedTemplate();

  const templateKey = selectedTemplateKey.value;
  const variationKey = selectedVariationKey.value;
  if (!templateKey) {
    allowNext.value = false;
    return false;
  }

  if (!order.value.id) {
    return false;
  }

  if (isSamePersistedSelection(templateKey, variationKey)) {
    allowNext.value = true;
    return true;
  }

  allowNext.value = false;
  try {
    await orderCreationStore.createPublication(order.value.id, templateKey, variationKey);
    allowNext.value = true;
    return true;
  } catch (err) {
    console.error('Error creating publication:', err);
    return false;
  }
}

async function ensurePublicationSaved(): Promise<boolean> {
  return persistSelectedTemplate();
}

async function changePublicationTemplate(templateKey: string, variationKey: string | null) {
  selectTemplate(templateKey, variationKey);
  void loadTemplateOptions(templateKey, variationKey);
  await persistSelectedTemplate();
}

defineExpose({ ensurePublicationSaved });

/* ---------------------------------- WATCH --------------------------------- */
// watch for changes in publicationTemplates and fetch base + variation preview images
watch(
  publicationTemplates,
  (templateGroups) => {
    if (templateGroups.length > 0) {
      fetchTemplatePreviews(templateGroups);
    }
  },
  { immediate: true }
);

watch(
  () => [publication.value?.template, publication.value?.variation] as const,
  ([templateKey, variationKey], [oldTemplateKey, oldVariationKey]) => {
    if (!templateKey) return;
    if (templateKey === oldTemplateKey && variationKey === oldVariationKey) return;
    selectedTemplateKey.value = templateKey;
    selectedVariationKey.value = variationKey ?? null;
    loadTemplateOptions(templateKey, variationKey ?? null);
  }
);
</script>

<style scoped>
.publication-template-step {
  height: 100%;
  background-color: #ffffff;
}

.left-section {
  flex: 0 0 40%;
  position: relative;
  height: 100%;
  border-right: 1px solid #eaecf0;
}

.header-area {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background-color: white;
  height: 120px;
}

.templates-area {
  position: absolute;
  top: 120px;
  bottom: 0;
  left: 0;
  right: 0;
  overflow-y: auto;
}

.publication-templates {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 30px;
}

.no-templates {
  padding: 30px;
}

.right-section {
  flex: 1 1 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 9px 11px;
  background-color: #ffffff;
  overflow: hidden;
}

.browser-chrome {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: 14px;
  gap: 6px;
  padding: 4px;
  margin-bottom: 10px;
  border-radius: 5px;
  background-color: #f5f5f5;
}

.browser-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #c5c5c5;
}

.iframe-wrapper {
  flex: 1 1 auto;
  display: flex;
  overflow: hidden;
  border-radius: 5px;
  border: 1px solid #eaecf0;
}

.publication-iframe {
  flex: 1 1 auto;
  width: 100%;
  border: none;
  transform-origin: top left;
}

.publication-iframe.dragging {
  pointer-events: none;
}

.template-wrapper .template-tags {
  margin-bottom: 20px;
  display: flex;
  gap: 5px;
  align-items: center;
}

.template-wrapper .template-options {
  display: flex;
  gap: 10px;
  align-items: center;
}

.template-option {
  flex: 0 0 128px;
  position: relative;
  height: 140px;
  cursor: pointer;
  transition: border-color 0.2s;
  border: 2px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
}

.template-option:hover {
  border-color: #cfd2d6;
}

.template-option.is-active {
  border: 2px solid var(--tw-color-primary);
  box-shadow: 0px 4px 8px 0px #00000029;
  position: relative;
  z-index: 1;
}

.template-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.template-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.template-fallback {
  width: 100%;
  height: 100%;
  background: #f0f0f0;
}

.template-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.4));
  pointer-events: none;
  z-index: 1;
}

.template-option .template-name {
  margin: 0;
  padding: 0;
  position: absolute;
  bottom: 5px;
  left: 5px;
  font-weight: 600;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  z-index: 2;
}
</style>
